'use client';
import { useState, useEffect, useMemo, useRef } from 'react';
import AiWatching from '../AiWatching';
import dynamic from 'next/dynamic';
import type { Layout } from 'react-grid-layout';
import { useAppStore } from '@/stores/app-store';
import { t } from '@/lib/i18n';
import { DashWidget } from '@/lib/types';
import WidgetRenderer, { widgetHeadIcon } from '../WidgetRenderer';
import { generateKSaverReport } from '@/lib/ksaver-report';
import { canEditDashboard } from '@/lib/dash-editor';
import AddWidgetModal from '../modals/AddWidgetModal';
import TemplateModal from '../modals/TemplateModal';
import WidgetConfigModal from '../modals/WidgetConfigModal';
import Modal from '../Modal';

const ResponsiveGridLayout = dynamic(
  () => import('react-grid-layout').then((m) => m.WidthProvider(m.Responsive)),
  { ssr: false },
);

const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const ROW_H = 30;
const MARGIN: [number, number] = [14, 14];

function sizeToW(size: 'w1' | 'w2' | 'w3'): number {
  return size === 'w1' ? 4 : size === 'w2' ? 6 : 12;
}

function ensureLayouts(widgets: DashWidget[]): Layout[] {
  let cursorX = 0;
  let cursorY = 0;
  let rowMaxH = 0;
  return widgets.map((wg, i) => {
    if (
      typeof wg.x === 'number' &&
      typeof wg.y === 'number' &&
      typeof wg.w === 'number' &&
      typeof wg.h === 'number'
    ) {
      return { i: String(i), x: wg.x, y: wg.y, w: wg.w, h: wg.h, minW: 3, minH: 4 };
    }
    const w = wg.w ?? sizeToW(wg.size);
    const h = wg.h ?? 6;
    if (cursorX + w > 12) {
      cursorX = 0;
      cursorY += rowMaxH;
      rowMaxH = 0;
    }
    const item: Layout = { i: String(i), x: cursorX, y: cursorY, w, h, minW: 3, minH: 4 };
    cursorX += w;
    rowMaxH = Math.max(rowMaxH, h);
    return item;
  });
}

export default function DashboardPage() {
  const {
    currentPlant,
    plants,
    lang,
    dashEditMode,
    setDashEditMode,
    dashConfigs,
    getDefaultWidgets,
    removeWidget,
    applyWidgetLayouts,
    resetDashboard,
  } = useAppStore();

  const [showAddWidget, setShowAddWidget] = useState(false);
  const [showWidgetConfig, setShowWidgetConfig] = useState(false);
  const [configWidgetIdx, setConfigWidgetIdx] = useState(0);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // Full-screen (SCADA wall) mode — the dashboard container fills the screen via
  // the Fullscreen API, so sidebar / topbar / browser chrome disappear.
  const dashRootRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else dashRootRef.current?.requestFullscreen?.();
  };

  const plant = currentPlant ? plants[currentPlant] : null;
  // K-saver sites expose a "Generate Report" action that builds the Performance
  // Test Summary Report PDF from the energy_saver device's data.
  const ksaverDevice = plant?.devices.find((dv) => dv.type === 'energy_saver') ?? null;
  const dbUser = useAppStore((s) => s.dbUser);
  const loadWidgets = useAppStore((s) => s.loadWidgets);

  useEffect(() => {
    if (!currentPlant) return;
    let cancelled = false;

    const init = async () => {
      if (dbUser) {
        await loadWidgets(currentPlant);
      }
      const current = useAppStore.getState().dashConfigs[currentPlant];
      // Demo-mode auto-upgrade: a water-system plant (Mspace V6) should default
      // to the SCADA schematic. If an older generic dashboard is cached for it
      // (e.g. seeded before the SCADA widget existed), regenerate so the SCADA
      // view shows without a manual Reset. Production (dbUser) is untouched —
      // its widgets come from the API.
      const devs = useAppStore.getState().plants[currentPlant]?.devices || [];
      const scadaCapable = devs.some(
        (d) => d.type === 'water_level' && (d.data?.level_ground !== undefined || d.data?.level_champ !== undefined),
      );
      const hasScada = Array.isArray(current) && current.some((w) => w.type === 'scada');
      const needDefaults = !current || current.length === 0 || (!dbUser && scadaCapable && !hasScada);
      if (!cancelled && needDefaults) {
        const defaults = getDefaultWidgets(currentPlant);
        if (defaults.length > 0) {
          useAppStore.setState((s) => ({ dashConfigs: { ...s.dashConfigs, [currentPlant]: defaults } }));
        }
      }
    };

    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlant]);

  const widgets = useMemo(
    () => (currentPlant ? dashConfigs[currentPlant] || [] : []),
    [currentPlant, dashConfigs],
  );

  // only the platform account edits dashboards (lib/dash-editor); demo mode (no dbUser) stays editable
  const canEditDash = !dbUser || canEditDashboard(dbUser.email);
  useEffect(() => { if (!canEditDash && dashEditMode) setDashEditMode(false); }, [canEditDash, dashEditMode, setDashEditMode]);
  // A widget flagged `fitViewport` (e.g. a full-page 3D twin) takes exactly the rows that fit
  // the window below the grid's top, so the whole HUD is visible without scrolling.
  // Not applied in edit mode, so a manual resize still saves its own height.
  const gridRef = useRef<HTMLDivElement>(null);
  // page-wide CSS zoom (globals.css: 90 % on desktop) — drag/resize deltas must be divided by it
  const [uiZoom, setUiZoom] = useState(1);
  useEffect(() => {
    const read = () => setUiZoom(parseFloat(getComputedStyle(document.documentElement).zoom) || 1);
    read();
    window.addEventListener('resize', read);
    return () => window.removeEventListener('resize', read);
  }, []);
  const [fitRows, setFitRows] = useState<number | null>(null);
  const anyFit = widgets.some((w) => (w as { fitViewport?: boolean }).fitViewport);
  useEffect(() => {
    if (!anyFit) return;
    const calc = () => {
      const el = gridRef.current;
      if (!el) return;
      // the page may be scaled with CSS zoom (globals.css --ui-zoom): getBoundingClientRect and
      // innerHeight are screen px, grid rows are CSS px
      const z = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
      const top = el.getBoundingClientRect().top;
      setFitRows(Math.max(12, Math.floor(((window.innerHeight - top) / z - 16 + MARGIN[1]) / (ROW_H + MARGIN[1]))));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [anyFit, isFullscreen, widgets.length]);
  const layout = useMemo(() => ensureLayouts(widgets).map((l) => (
    fitRows && !dashEditMode && (widgets[parseInt(l.i, 10)] as { fitViewport?: boolean } | undefined)?.fitViewport ? { ...l, h: fitRows } : l
  )), [widgets, fitRows, dashEditMode]);

  const handleLayoutChange = (newLayout: Layout[]) => {
    if (!currentPlant || !dashEditMode) return;
    const updates = newLayout.map((l) => ({
      idx: parseInt(l.i, 10),
      x: l.x,
      y: l.y,
      w: l.w,
      h: l.h,
    }));
    applyWidgetLayouts(currentPlant, updates);
  };

  if (!currentPlant || !plant) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        color: 'var(--text-muted)',
      }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.25, marginBottom: 16 }}>
          <rect x="3" y="3" width="7" height="9" rx="2" /><rect x="14" y="3" width="7" height="5" rx="2" />
          <rect x="14" y="12" width="7" height="9" rx="2" /><rect x="3" y="16" width="7" height="5" rx="2" />
        </svg>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{t('selectPlant', lang)}</div>
        <div style={{ fontSize: 12, marginTop: 4, opacity: 0.6 }}>Choose a plant from the sidebar to view its dashboard</div>
      </div>
    );
  }

  const widgetTypeIcons: Record<string, { icon: React.ReactNode; color: string }> = {
    single_card: { color: 'var(--amber)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="3" /><circle cx="9" cy="12" r="2.5" /><line x1="14" y1="10" x2="18" y2="10" /><line x1="14" y1="14" x2="17" y2="14" /></svg> },
    card: { color: 'var(--blue)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3" /><line x1="7" y1="8" x2="12" y2="8" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="7" y1="16" x2="14" y2="16" /></svg> },
    status: { color: 'var(--green)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" fill="currentColor" /></svg> },
    device_list: { color: 'var(--cyan)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="4" cy="6" r="1" fill="currentColor" /><circle cx="4" cy="12" r="1" fill="currentColor" /><circle cx="4" cy="18" r="1" fill="currentColor" /></svg> },
    flow_graph: { color: 'var(--purple)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="3" /><circle cx="19" cy="12" r="3" /><line x1="8" y1="12" x2="16" y2="12" /><polyline points="13 9 16 12 13 15" /></svg> },
    accum_card: { color: 'var(--amber)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 8 12 12 15 17 8 21 4" /><polyline points="17 4 21 4 21 8" /></svg> },
    gauge: { color: 'var(--green)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22a10 10 0 0 1-7.07-17.07" /><path d="M12 22a10 10 0 0 0 7.07-17.07" /><path d="M12 14l-3-5" /></svg> },
    line_chart: { color: 'var(--blue)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 8 14 14 10 10 6 16 2 12" /></svg> },
    bar_chart: { color: 'var(--cyan)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="4" height="10" rx="1" /><rect x="10" y="4" width="4" height="16" rx="1" /><rect x="16" y="8" width="4" height="12" rx="1" /></svg> },
    donut_chart: { color: 'var(--purple)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 0 1 10 10" /><path d="M12 2a10 10 0 0 0 0 20 10 10 0 0 0 8.66-5" /><circle cx="12" cy="12" r="4" /></svg> },
    solar_overview: { color: 'var(--amber)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /></svg> },
    energy_balance: { color: 'var(--blue)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg> },
    env_overview: { color: 'var(--green)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" /></svg> },
    air_quality: { color: 'var(--cyan)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14s1-2 4-2 5 4 8 4 4-2 4-2" /><path d="M4 10s1-2 4-2 5 4 8 4 4-2 4-2" /></svg> },
    alert_summary: { color: 'var(--red)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /></svg> },
    ai_health: { color: 'var(--purple)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="4" /><circle cx="9" cy="10" r="1.5" fill="currentColor" /><circle cx="15" cy="10" r="1.5" fill="currentColor" /><path d="M9 16c1 1 5 1 6 0" /></svg> },
    water_level: { color: 'var(--cyan)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.7c0 0-8 7.3-8 13.3a8 8 0 0 0 16 0c0-6-8-13.3-8-13.3z" /><path d="M6 16h12" /></svg> },
    scada: { color: 'var(--cyan)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="12" width="6" height="9" rx="1" /><path d="M3 16h6" /><rect x="15" y="3" width="6" height="7" rx="1" /><path d="M15 7h6" /><circle cx="12" cy="18" r="2" /><path d="M9 14h3M14 14l-2 3M18 10v3" /></svg> },
    image: { color: 'var(--amber)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg> },
    energy_hub_scada: { color: 'var(--amber)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="6" rx="1.5" /><path d="M12 8v4M4 12h16M4 12v4M12 12v4M20 12v4" /><rect x="2" y="16" width="4" height="5" rx="1" /><rect x="10" y="16" width="4" height="5" rx="1" /><rect x="18" y="16" width="4" height="5" rx="1" /></svg> },
    site_energy_map: { color: 'var(--green)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" /></svg> },
    resort_ops: { color: 'var(--blue)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M5 21V8l7-5 7 5v13" /><rect x="9" y="12" width="6" height="9" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg> },
    or_monitor: { color: 'var(--red)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M12 7v10M7 12h10" /></svg> },
    genset_monitor: { color: 'var(--amber)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="12" rx="2" /><circle cx="8" cy="13" r="3" /><path d="M15 11h4M15 15h4M6 7V5h4v2" /></svg> },
    energy_usage_table: { color: 'var(--cyan)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="10" y1="4" x2="10" y2="20" /></svg> },
    time: { color: 'var(--blue)', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg> },
  };

  const primaryBtn: React.CSSProperties = {
    padding: '7px 18px',
    borderRadius: 10,
    border: 'none',
    background: 'linear-gradient(135deg, var(--btn-from, var(--amber)), var(--gold))',
    color: '#0C1222',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 2px 12px var(--gold-glow)',
    transition: 'all .3s cubic-bezier(.16,1,.3,1)',
  };

  const ghostBtn: React.CSSProperties = {
    padding: '7px 18px',
    borderRadius: 10,
    border: '1px solid var(--glass-border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all .3s cubic-bezier(.16,1,.3,1)',
  };

  return (
    <div ref={dashRootRef} style={{ padding: 0, ...(isFullscreen ? { background: 'var(--bg-base)', height: 'var(--vh100)', overflowY: 'auto', padding: 16 } : {}) }}>
      {/* ── Header ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 20,
        flexWrap: 'wrap',
        gap: 12,
      }}>
        {/* wrap ด้วย เพราะชื่อไซต์ยาว ๆ บวกป้ายสองอันล้นบนจอแคบ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="9" rx="2" /><rect x="14" y="3" width="7" height="5" rx="2" />
            <rect x="14" y="12" width="7" height="9" rx="2" /><rect x="3" y="16" width="7" height="5" rx="2" />
          </svg>
          <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
            {plant.name}
          </span>
          <span style={{
            padding: '2px 10px',
            borderRadius: 99,
            fontSize: 10,
            fontWeight: 700,
            background: 'var(--gold-dim)',
            color: 'var(--gold)',
          }}>
            {widgets.length} widgets
          </span>
          {/* สัญญาณว่ามีระบบเฝ้าอยู่ตลอด พร้อมผลที่มันเห็นจริง ณ ตอนนี้ */}
          <AiWatching plant={plant} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {dashEditMode ? (
            <>
              <button onClick={() => setDashEditMode(false)} style={primaryBtn}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Lock
              </button>
              <button onClick={() => setShowResetConfirm(true)} style={ghostBtn}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
                Reset
              </button>
            </>
          ) : (
            <>
              {ksaverDevice && plant && (
                <button
                  onClick={() => generateKSaverReport(plant, ksaverDevice)}
                  style={{ ...primaryBtn, background: 'var(--green)', borderColor: 'var(--green)' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                    <line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="14" y2="17" />
                  </svg>
                  {lang === 'th' ? 'สร้างรายงาน' : 'Generate Report'}
                </button>
              )}
              <button onClick={toggleFullscreen} style={ghostBtn} title={isFullscreen ? (lang === 'th' ? 'ออกจากเต็มจอ' : 'Exit full screen') : (lang === 'th' ? 'เต็มจอ' : 'Full screen')}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  {isFullscreen
                    ? <><path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" /><path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" /></>
                    : <><path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" /></>}
                </svg>
                {isFullscreen ? (lang === 'th' ? 'ออกเต็มจอ' : 'Exit') : (lang === 'th' ? 'เต็มจอ' : 'Full screen')}
              </button>
              {canEditDash && <>
              <button onClick={() => setShowTemplates(true)} style={ghostBtn}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
                </svg>
                {lang === 'th' ? 'เทมเพลต' : 'Templates'}
              </button>
              <button onClick={() => setShowAddWidget(true)} style={primaryBtn}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {lang === 'th' ? 'เพิ่มวิดเจ็ต' : 'Add Widget'}
              </button>
              <button onClick={() => setDashEditMode(true)} style={ghostBtn}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                {lang === 'th' ? 'แก้ไข' : 'Edit'}
              </button>
              </>}
            </>
          )}
        </div>
      </div>

      {/* ── Edit Mode Banner ── */}
      {dashEditMode && (
        <div style={{
          background: 'var(--gold-dim)',
          border: '1px solid rgba(0,229,160,0.2)',
          borderRadius: 12,
          padding: '12px 18px',
          marginBottom: 20,
          fontSize: 12,
          color: 'var(--gold)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          Edit Mode — Drag the gold handle (top-left) to move, drag the bottom-right corner to resize.
        </div>
      )}

      {/* ── Widget Grid ── */}
      {widgets.length > 0 && (
        <div ref={gridRef} className={`dash-grid${dashEditMode ? ' editing-mode' : ''}`}>
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
            breakpoints={BREAKPOINTS}
            cols={COLS}
            rowHeight={ROW_H}
            margin={MARGIN}
            transformScale={uiZoom}
            containerPadding={[0, 0]}
            isDraggable={dashEditMode}
            isResizable={dashEditMode}
            draggableHandle=".w-drag"
            compactType="vertical"
            preventCollision={false}
            onLayoutChange={handleLayoutChange}
          >
            {widgets.map((widget, idx) => (
              <div
                key={String(idx)}
                className={`dash-widget${dashEditMode ? ' editing' : ''}`}
                style={widget.bgColor ? { background: widget.bgColor } : undefined}
              >
                {dashEditMode && (
                  <>
                    <div className="w-drag" title="Drag to move">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="9" cy="6" r="1" fill="currentColor" /><circle cx="15" cy="6" r="1" fill="currentColor" />
                        <circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" />
                        <circle cx="9" cy="18" r="1" fill="currentColor" /><circle cx="15" cy="18" r="1" fill="currentColor" />
                      </svg>
                    </div>
                    <button
                      className="w-settings"
                      title="Settings"
                      onClick={() => { setConfigWidgetIdx(idx); setShowWidgetConfig(true); }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                      </svg>
                    </button>
                    <button
                      className="w-remove"
                      title="Remove"
                      onClick={() => currentPlant && removeWidget(currentPlant, idx)}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </>
                )}

                {/* ── Widget Header (single_card renders its own label inline) ── */}
                {widget.type !== 'single_card' && (
                  <div className="wh">
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {(() => {
                        // Prefer an icon matching the metric (temp/power/energy …);
                        // fall back to the per-widget-type icon.
                        const head = widgetHeadIcon(widget) ?? widgetTypeIcons[widget.type];
                        return head ? (
                          <span style={{
                            width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                            background: `color-mix(in srgb, ${head.color} 12%, transparent)`,
                            color: head.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {head.icon}
                          </span>
                        ) : (
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--gold)', flexShrink: 0 }} />
                        );
                      })()}
                      {widget.title}
                    </span>
                  </div>
                )}

                {/* ── Widget Body ── */}
                <div className="wb">
                  <WidgetRenderer widget={widget} plant={plant} />
                </div>
              </div>
            ))}
          </ResponsiveGridLayout>
        </div>
      )}

      {/* ── Empty state ── */}
      {widgets.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--text-muted)',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.2, marginBottom: 16 }}>
            <rect x="3" y="3" width="7" height="9" rx="2" /><rect x="14" y="3" width="7" height="5" rx="2" />
            <rect x="14" y="12" width="7" height="9" rx="2" /><rect x="3" y="16" width="7" height="5" rx="2" />
          </svg>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
            {lang === 'th' ? 'ยังไม่มีวิดเจ็ต' : 'Nothing on this dashboard yet'}
          </div>
          <div style={{ fontSize: 12, opacity: 0.6 }}>
            {canEditDash
              ? (lang === 'th' ? 'กด "เพิ่มวิดเจ็ต" เพื่อเลือกค่าที่อยากเห็นทุกวัน' : 'Click "Add Widget" to pick the values you want to see every day')
              : (lang === 'th' ? 'ติดต่อเจ้าของหรือผู้ดูแลไซต์นี้เพื่อจัดหน้าแดชบอร์ด' : 'Ask the site owner or admin to set up this dashboard')}
          </div>
        </div>
      )}
      <TemplateModal isOpen={showTemplates} onClose={() => setShowTemplates(false)} />

      <AddWidgetModal
        isOpen={showAddWidget}
        onClose={() => setShowAddWidget(false)}
        plantId={currentPlant!}
        onWidgetAdded={(newIdx: number) => {
          setShowAddWidget(false);
          setTimeout(() => {
            setConfigWidgetIdx(newIdx);
            setShowWidgetConfig(true);
          }, 100);
        }}
      />
      {showWidgetConfig && widgets[configWidgetIdx] && (
        <WidgetConfigModal isOpen={showWidgetConfig} onClose={() => setShowWidgetConfig(false)} plantId={currentPlant!} widgetIndex={configWidgetIdx} widget={widgets[configWidgetIdx]} />
      )}
      <Modal isOpen={showResetConfirm} onClose={() => setShowResetConfirm(false)} title={t('resetDashboard', lang)} maxWidth={420}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 22px' }}>
          {t('resetDashboardConfirm', lang)}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={() => setShowResetConfirm(false)} style={ghostBtn}>
            {t('cancel', lang)}
          </button>
          <button
            onClick={() => {
              if (currentPlant) resetDashboard(currentPlant);
              setShowResetConfirm(false);
            }}
            style={{ ...primaryBtn, background: 'var(--red)', color: '#fff', boxShadow: 'none' }}
          >
            {t('resetDashboard', lang)}
          </button>
        </div>
      </Modal>
    </div>
  );
}
